import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Method,
  State
} from "@stencil/core";
import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { ActionMenuModel } from "@genexus/chameleon-controls-library";

import { Locale } from "../../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "chameleon/scrollbar",
  "components/button",
  "components/dropdown",
  "components/icon",
  "utils/elevation"
];

const COPY_ICON = getIconPath({
  category: "system",
  name: "copy",
  colorType: "primary"
});
const DELETE_ICON = getIconPath({
  category: "system",
  name: "delete-outlined",
  colorType: "primary"
});
const CONVERSATION_ICON = getIconPath({
  category: "objects",
  name: "conversational-flows"
});
const MORE_HORIZONTAL_ICON = getIconPath({
  category: "system",
  name: "more-horizontal",
  colorType: "interactive"
});

@Component({
  tag: "gx-ide-chat-container",
  styleUrl: "chat-container.scss",
  shadow: false,
  assetsDirs: ["gx-ide-assets/chat-container"]
})
export class GxIdeChatContainer {
  #menuItems: ActionMenuModel = [
    { type: "slot", id: "delete-chat-button" },
    { type: "slot", id: "copy-chat-button" }
  ];

  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  @Element() el: HTMLGxIdeChatContainerElement;

  /**
   * Coneration Copied
   */
  @State() displayConversationCopiedMessage: boolean = false;

  /**
   * Displays or hides the menu
   */
  @State() menuIsExpanded: boolean = false;

  /**
   * The chat title
   */
  @Prop() readonly chatTitle?: string;

  /**
   * If true a button for copying the conversation will be render on the header
   */
  @Prop() readonly copyConversationCallback: () => void;

  /**
   * If true a button for deleting the conversation will be render on the header
   */
  @Prop() readonly deleteConversationCallback: () => void;

  /**
   * If true a the welcome screen will be rendered
   */
  @Prop() readonly displayWelcomeScreen: boolean = false;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  /**
   * It will display a feedback message that the conversation has been copied
   */
  @Method()
  async showCopyConversationMessage() {
    this.displayConversationCopiedMessage = true;
    setTimeout(() => {
      this.displayConversationCopiedMessage = false;
    }, 2000);
  }

  #showMenuHandler = () => {
    this.menuIsExpanded = true;
  };

  #menuExpandedChangeHandler = (event: CustomEvent<boolean>) => {
    this.menuIsExpanded = event.detail;
  };

  #copyConversationHandler = () => {
    if (this.copyConversationCallback) {
      this.copyConversationCallback();
      this.menuIsExpanded = false;
    }
  };
  #deleteConversationHandler = () => {
    if (this.deleteConversationCallback) {
      this.deleteConversationCallback();
      this.menuIsExpanded = false;
    }
  };

  render() {
    const renderHeader =
      this.chatTitle ||
      this.copyConversationCallback ||
      this.deleteConversationCallback;

    const renderButtonsGroup =
      this.copyConversationCallback || this.deleteConversationCallback;
    return (
      <Host
        class={{
          "chat-container": true,
          "with-header": !!renderHeader
        }}
      >
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          {renderHeader && (
            <header class="header caption-semi-bold-l">
              {this.chatTitle && this.chatTitle}
              {renderButtonsGroup && (
                <ch-action-menu-render
                  class="dropdown"
                  blockAlign="inside-start"
                  inlineAlign="inside-end"
                  model={this.#menuItems}
                  expanded={this.menuIsExpanded}
                  onExpandedChange={this.#menuExpandedChangeHandler}
                >
                  <button
                    class="button-tertiary button-icon-only"
                    onClick={this.#showMenuHandler}
                    type="button"
                    aria-label={this.#componentLocale.showMenuAriaLabel}
                    title={this.#componentLocale.showMenuAriaLabel}
                  >
                    <ch-image
                      class="icon-m"
                      src={MORE_HORIZONTAL_ICON}
                    ></ch-image>
                  </button>
                  {this.copyConversationCallback && (
                    <button
                      class="button-tertiary button-icon-and-text button-copy-chat"
                      slot="copy-chat-button"
                      onClick={this.#copyConversationHandler}
                      type="button"
                      aria-label={
                        this.#componentLocale.copyConversationAriaLabel
                      }
                      title={this.#componentLocale.copyConversationAriaLabel}
                    >
                      <ch-image class="icon-m" src={COPY_ICON}></ch-image>
                      {this.#componentLocale.copyConversationLabel}
                    </button>
                  )}
                  {this.deleteConversationCallback && (
                    <button
                      class="button-tertiary button-icon-and-text button-delete-chat"
                      slot="delete-chat-button"
                      onClick={this.#deleteConversationHandler}
                      type="button"
                      aria-label={
                        this.#componentLocale.deleteConversationAriaLabel
                      }
                      title={this.#componentLocale.deleteConversationAriaLabel}
                    >
                      <ch-image class="icon-m" src={DELETE_ICON}></ch-image>
                      {this.#componentLocale.clearConversationLabel}
                    </button>
                  )}
                </ch-action-menu-render>
              )}
            </header>
          )}
          <section class="ch-chat-container">
            <slot></slot>
            {this.displayConversationCopiedMessage && (
              <div class="chat-container__conversation-copied">
                <div class="chat-container__conversation-copied-wrapper">
                  <ch-image class="icon-m" src={CONVERSATION_ICON}></ch-image>
                  <p class="body-regular-l">conversation copied</p>
                </div>
              </div>
            )}
          </section>
          {/* <footer class="footer elevation-1">footer</footer> */}
        </section>
      </Host>
    );
  }
}
